import React, { useEffect, useState } from "react";
import Editor from "@monaco-editor/react";
import Notification from "../../components/Notification";
import axios from "axios";
import { Toaster, toast } from 'react-hot-toast';
import { useGeneralContext } from "../../contexts/GeneralContext";

const Judge = () => {
        const [language, setLanguage] = useState("python");
        const [codeText, setCodeText] = useState("");
        const [question, setQuestion] = useState("");
        const [candidate, setCandidate] = useState("");
        const languages = {
                python: { name: "Python", extension: ".py", theme: "python" },
                cpp20: { name: "C++20", extension: ".cpp", theme: "cpp" },
                cpp17: { name: "C++17", extension: ".cpp", theme: "cpp" },
        };

        const [valid, setValid] = useState(false);
        const [waiting, setWaiting] = useState(false);

        const [questions, setQuestions] = useState([]);
        const [candidates, setCandidates] = useState([]);

        const [results, setResults] = useState(null);

        const generals = useGeneralContext();
        const ID_OFFSET = generals.ID_OFFSET;

        const handleFileInput = (e) => {
                const file = e.target.files[0];
                if (file) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                                const fileExtension = file.name.split('.').pop();
                                const matchingLanguages = Object.entries(languages).filter(
                                        ([_, lang]) => lang.extension === `.${fileExtension}`
                                );
                                const selectedLanguage = matchingLanguages.length > 0 ? matchingLanguages[0] : null;
                                if (selectedLanguage) {
                                        const [langKey] = selectedLanguage;
                                        setLanguage(langKey);
                                        setCodeText(event.target.result);
                                } else {
                                        alert("Language not identified.");
                                }

                        };
                        reader.readAsText(file);
                }
        };

        const handleEditorChange = (value, _) => {
                setCodeText(value.trim());
        };

        const fetchQuestions = async () => {
                try {
                        const res = await axios.get('/api/judge/questions', {
                                withCredentials: true
                        });
                        setQuestions(res.data);
                        setQuestion(res.data[0]);
                } catch (err) {
                        alert(`Failed to fetch questions for judging: ${err.message}`)
                }
        };

        const fetchCandidates = async () => {
                try {
                        const res = await axios.get('/api/candidates/', {
                                withCredentials: true
                        });
                        setCandidates(res.data);
                } catch (err) {
                        alert(`Failed to fetch candidates for judging: ${err.message}`)
                }
        };

        const handleJudgeSubmit = async () => {
                if (!valid) return;

                setWaiting(true);
                setTimeout(() => setWaiting(false), 3000);

                try {
                        const res = await axios.post('/api/judge/score', {
                                language,
                                code: codeText,
                                question,
                                candidate
                        }, {
                                withCredentials: true
                        });
                        setResults(res.data);
                        if (candidate) {
                                toast.success(
                                        <span>
                                                Candidate marks updated successfully.
                                        </span>,
                                        {
                                                style: { maxWidth: '500px' },
                                        }
                                )
                        }
                } catch (err) {
                        alert(`Failed to mark code: ${err.message}`);
                }
        }

        useEffect(() => {
                fetchQuestions();
                fetchCandidates();
        }, []);

        useEffect(() => {
                setValid(language && codeText && question);
        }, [language, codeText]);

        const statusMap = {
                full: "Accepted",
                partial: "Partial",
                fail: "Failed"
        };
        return (
                <div className="judge">
                        <Toaster position="top-center" />
                        <div className="judge-left-panel">
                                Upload code file or paste code into viewer.
                                <input
                                        className="judge-file-select"
                                        type="file"
                                        accept={Object.values(languages).map((lang) => lang.extension).join(",")}
                                        onChange={handleFileInput}
                                />
                                <div className="judge-setting">
                                        <div>Select Question</div>
                                        <select
                                                className="judge-select"
                                                value={question}
                                                onChange={(e) => setQuestion(e.target.value)}
                                        >
                                                {questions.map((q, idx) => (
                                                        <option key={idx} value={q}>{q}</option>
                                                ))}
                                        </select>
                                </div>
                                <div className="judge-setting">
                                        <div>
                                                Candidate Number&nbsp;
                                                <span style={{ color: "#7A7A7A", fontSize: "12px" }}>(Optional)</span>
                                        </div>
                                        <select
                                                className="judge-select"
                                                value={candidate}
                                                onChange={(e) => setCandidate(e.target.value)}
                                        >
                                                <option value="" selected>None</option>
                                                {candidates.map((c) => (
                                                        <option key={c.id} value={c.id}>{c.id + ID_OFFSET}</option>
                                                ))}
                                        </select>
                                </div>
                                <div className="settings-input-hint">Selecting a student adds the marks to their score automatically.</div>
                                <div
                                        className={`judge-submit ${!valid || waiting ? 'judge-submit-disabled' : ''}`}
                                        onClick={handleJudgeSubmit}
                                >
                                        Mark Code
                                </div>
                                <div className="judge-results">
                                        <div className="judge-results-title">Results</div>
                                        <div className="judge-results-page">
                                                {results ? <>
                                                        <div className={`results-total total-${results.status}`}>{results?.totalScore} / {results?.maxScore} marks</div>
                                                        <div className={`results-summary summary-${results.status}`}>
                                                                {statusMap[results.status] || ""} Solution
                                                        </div>
                                                        <div className="results-summary-subtext">Passed {results?.passedCases} out of {results?.totalCases} cases</div>
                                                </> : <>No code submitted</>}
                                        </div>
                                        <div className="judge-results-cases">
                                                {results?.results ? results.results.map((test, idx) => (
                                                        <div className="test-case" key={idx}>
                                                                <img
                                                                        src={test.isCorrect ? "../icons/test-pass.svg" : "../icons/test-fail.svg"}
                                                                        className={test.isCorrect ? "" : "test-fail-icon"}
                                                                        alt={test.isCorrect ? "Pass" : "Fail"}
                                                                />
                                                                Test Case {test.index}
                                                                <div className={test.isCorrect ? "test-case-passtext" : "test-case-failtext"}>
                                                                        {test.isCorrect ? "Correct Answer" : (test.isWithinTimeLimit ? "Wrong Answer" : "Time Limit Exceeded")}
                                                                </div>
                                                        </div>
                                                )) : <div className="judge-results-placeholder">
                                                        Submit code by pasting or uploading a file.
                                                </div>}
                                        </div>
                                </div>
                        </div>
                        <div className="judge-right-panel">
                                <Editor
                                        height="100%"
                                        width="100%"
                                        language={languages[language].theme}
                                        onValidate={() => { }}
                                        value={codeText}
                                        theme="vs-dark"
                                        onChange={handleEditorChange}
                                />
                                <select
                                        className="judge-language judge-select"
                                        value={language}
                                        onChange={(e) => setLanguage(e.target.value)}
                                >
                                        {Object.entries(languages).map(([key, lang]) => (
                                                <option key={key} value={key}>
                                                        {lang.name}
                                                </option>
                                        ))}
                                </select>
                        </div>
                </div>
                // <Notification text="This page is coming soon." subtext="The code judge will open when round one commences." />
        );
};

export default Judge;